import React, { forwardRef, CSSProperties, useMemo, useImperativeHandle } from "react";
import { Calendar, CalendarProps } from "primereact/calendar";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import { MultiSelect, MultiSelectProps } from "primereact/multiselect";
import { InputText, InputTextProps } from "primereact/inputtext";
import { InputTextarea, InputTextareaProps } from "primereact/inputtextarea";
import { Password, PasswordProps } from "primereact/password";
import { InputNumber, InputNumberProps } from "primereact/inputnumber";

import { getColorBaseTokenValue, mergeClassNames } from "../../design-tokens";
import styles from "./Input.module.css";

export type InputType = "text" | "number" | "password" | "calendar" | "calendarRange" | "select" | "multiselect" | "textArea";

export interface InputOption {
  id?: number | string;
  description?: string;
  [key: string]: any;
}

export interface InputProps
  extends Omit<InputTextProps, "type" | "value" | "onChange" | "pt" | "ptOptions"> {
  /**
   * Tipo do input (text, calendar, select, multiselect, calendarRange)
   */
  type: InputType;

  /**
   * Label do campo
   */
  label: string;

  /**
   * Valor atual do input
   */
  value: any;

  /**
   * Callback quando o valor muda
   */
  onChange: (value: any) => void;

  /**
   * Opções para select/multiselect
   */
  options?: InputOption[];

  /**
   * Indica se o campo é obrigatório
   * @default false
   */
  required?: boolean;

  /**
   * Mostra indicador de loading (para selects)
   * @default false
   */
  loading?: boolean;

  /**
   * Formato da data
   * @default "dd/mm/yy"
   */
  dateFormat?: string;

  /**
   * Modo de seleção do calendar
   * @default "single"
   */
  selectionMode?: "single" | "multiple" | "range";

  /**
   * Mostra seletor de hora no calendar
   * @default false
   */
  showTime?: boolean;

  /**
   * Máximo de labels a mostrar no multiselect
   * @default 3
   */
  maxSelectedLabels?: number;

  /**
   * Propriedade do objeto a usar como label
   * @default "description"
   */
  optionLabel?: string;

  /**
   * Propriedade do objeto a usar como value
   */
  optionValue?: string;

  /**
   * Número de linhas para textArea
   * @default 5
   */
  rows?: number;

  /**
   * Número de colunas para textArea
   */
  cols?: number;
  
  /**
   * Mostra toggle de visibilidade (apenas para password)
   * @default true
   */
  toggleMask?: boolean;

  /**
   * Mostra feedback de força da senha (apenas para password)
   * @default false
   */
  feedback?: boolean;

  /**
   * Nome do ícone Material Symbols
   */
  icon?: string;

  /**
   * Conteúdo extra exibido ao lado do label (ex: links)
   */
  labelAction?: React.ReactNode;

  /**
   * Custom templates para o dropdown
   */
  valueTemplate?: any;
  itemTemplate?: any;
  showClear?: boolean;
  filter?: boolean;
}

const Input = forwardRef<any, InputProps>(
  (
    {
      type = "text",
      label,
      placeholder = "Selecione",
      value,
      onChange,
      options = [],
      disabled = false,
      required = false,
      loading = false,
      dateFormat = "dd/mm/yy",
      selectionMode = "single",
      showTime = false,
      maxSelectedLabels = 3,
      optionLabel = "description",
      optionValue,
      rows = 5,
      cols,
      className,
      icon,
      labelAction,
      toggleMask = true,
      feedback = false,
      ...restProps
    },
    ref
  ) => {
    const innerRef = React.useRef<any>(null);

    useImperativeHandle(ref, () => innerRef.current);

    const styleVariables = useMemo(
      (): CSSProperties => ({
        "--ds-input-label-color": getColorBaseTokenValue("color.neutral.900"),
        "--ds-input-required-color": getColorBaseTokenValue("color.feedback.danger.700"),
      } as CSSProperties),
      []
    );

    const effectiveOptionLabel = useMemo(() => {
      if (optionLabel !== "description") return optionLabel;
      if (options.length > 0 && !options[0].description && options[0].label) {
        return "label";
      }
      return "description";
    }, [optionLabel, options]);

    const renderField = (): React.ReactNode => {
      const fieldClassName = mergeClassNames(styles.field, className);

      switch (type) {
        case "calendar": {
          const calendarProps: CalendarProps = {
            value,
            onChange: (e: any) => onChange(e.value),
            placeholder,
            disabled,
            showTime,
            selectionMode,
            className: fieldClassName,
          };
          return <Calendar ref={innerRef} {...calendarProps} />;
        }

        case "calendarRange": {
          const calendarRangeProps: CalendarProps = {
            value,
            onChange: (e: any) => onChange(e.value),
            placeholder,
            disabled,
            selectionMode: "range",
            dateFormat,
            className: fieldClassName,
          };
          return <Calendar ref={innerRef} {...calendarRangeProps} />;
        }

        case "select": {
          const dropdownProps: DropdownProps = {
            ...(restProps as any),
            value,
            options,
            onChange: (e: any) => onChange(e.value),
            placeholder,
            disabled,
            loading,
            optionLabel: effectiveOptionLabel,
            optionValue,
            className: fieldClassName,
          };
          return <Dropdown ref={innerRef} {...dropdownProps} />;
        }

        case "multiselect": {
          const multiselectProps: MultiSelectProps = {
            value,
            options,
            onChange: (e: any) => onChange(e.value),
            placeholder,
            disabled,
            optionLabel: effectiveOptionLabel,
            optionValue,
            maxSelectedLabels,
            className: fieldClassName,
          };
          return <MultiSelect ref={innerRef} {...multiselectProps} />;
        }

        case "number": {
          const numberProps: InputNumberProps = {
            ...(restProps as any),
            value: value ?? null,
            onValueChange: (e) => onChange(e.value),
            placeholder,
            disabled,
            className: fieldClassName,
            useGrouping: false,
          };
          const input = <InputNumber ref={innerRef} {...numberProps} />;

          if (icon) {
            return (
              <span className="p-input-icon-left" style={{ width: "100%" }}>
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: "20px",
                    color: "var(--color-neutral-500)",
                    zIndex: 1,
                  }}
                >
                  {icon}
                </span>
                {input}
              </span>
            );
          }

          return input;
        }

        case "password": {
          const passwordProps: PasswordProps = {
            ...(restProps as any),
            value: value ?? "",
            onChange: (e) => onChange(e.target.value || null),
            placeholder,
            disabled,
            toggleMask,
            feedback,
            className: fieldClassName,
            autoComplete: "current-password",
          };
          const input = <Password ref={innerRef} {...passwordProps} />;

          if (icon) {
            return (
              <span className="p-input-icon-left" style={{ width: "100%" }}>
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: "20px",
                    color: "var(--color-neutral-500)",
                    zIndex: 1, // Garante que o ícone fique sobre o input
                  }}
                >
                  {icon}
                </span>
                {input}
              </span>
            );
          }

          return input;
        }

        case "textArea": {
          const textAreaProps: InputTextareaProps = {
            ...(restProps as any),
            value: value ?? "",
            onChange: (e) => onChange(e.target.value || null),
            placeholder,
            disabled,
            rows,
            cols,
            className: fieldClassName,
            autoResize: true,
          };
          return <InputTextarea ref={innerRef} {...textAreaProps} />;
        }

        case "text":
        default: {
          const inputProps: InputTextProps = {
            ...restProps,
            value: value ?? "",
            onChange: (e) => onChange(e.target.value || null),
            placeholder,
            disabled,
            className: fieldClassName,
          };
          const input = <InputText ref={innerRef} {...inputProps} />;

          if (icon) {
            return (
              <span className="p-input-icon-left" style={{ width: "100%" }}>
                <span className="material-symbols-outlined" style={{ fontSize: "20px", color: "var(--color-neutral-500)" }}>
                  {icon}
                </span>
                {input}
              </span>
            );
          }

          return input;
        }
      }
    };

    return (
      <div className={styles.root} style={styleVariables}>
        <div className={styles.labelRow}>
          <label className={styles.label}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </label>
          {labelAction && <div className={styles.labelAction}>{labelAction}</div>}
        </div>
        {renderField()}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
